import type { Locale, RouteKey } from "@/lib/routes";
import { ROUTES } from "@/lib/routes";

const BASE_URL = "https://onepractix.com";
const ORG_ID = `${BASE_URL}/#organization`;
const WEBSITE_ID = `${BASE_URL}/#website`;
const MORITZ_ID = `${BASE_URL}/de/ueber-uns#moritz-buchgeher`;
const BOB_ID = `${BASE_URL}/de/ueber-uns#bob-van-den-boog`;

function pathFor(key: RouteKey, locale: Locale): string {
  const entry = ROUTES.find((r) => r.key === key);
  return entry ? entry[locale] : "/";
}

function urlFor(key: RouteKey, locale: Locale): string {
  return `${BASE_URL}${pathFor(key, locale)}`;
}

/**
 * Centralized bilingual SEO content for all main pages.
 * Page components read titles/descriptions from here via `getSEOContent`.
 */
export interface PageSEO {
  title: string;
  description: string;
}

export const SEO_CONTENT: Record<string, { de: PageSEO; en: PageSEO }> = {
  home: {
    de: {
      title: "Weniger Verwaltung für Praxen | OnePractix",
      description:
        "OnePractix kümmert sich um Websites, Termine und Anrufe für Praxen und Kliniken, damit Ihr Team weniger unterbrochen wird.",
    },
    en: {
      title: "Less admin for medical practices | OnePractix",
      description:
        "OnePractix takes care of websites, appointments and phone calls for medical practices and clinics, so your team faces fewer interruptions.",
    },
  },
  services: {
    de: {
      title: "Leistungen für Praxen | OnePractix",
      description:
        "Websites, Online-Terminbuchung, Telefonservice und Sichtbarkeit für Praxen, strukturiert betreut aus einer Hand, damit Abläufe ruhiger werden.",
    },
    en: {
      title: "Services for medical practices | OnePractix",
      description:
        "Websites, appointment booking, phone handling and visibility for medical practices, structured and looked after by one accountable partner.",
    },
  },
  website: {
    de: {
      title: "Website für Praxen – klar betreut | OnePractix",
      description:
        "Eine Website für Praxen, die Patienten klar informiert, Kontakt erleichtert und im Praxisalltag laufend verlässlich betreut wird.",
    },
    en: {
      title: "Medical practice websites | OnePractix",
      description:
        "Medical practice websites that clearly guide patients, make contact easier and are reliably maintained in the background.",
    },
  },
  booking: {
    de: {
      title: "Online-Terminbuchung für Praxen | OnePractix",
      description:
        "Online-Terminbuchung für Praxen, die Termine, Anfragen und Erreichbarkeit strukturiert und Unterbrechungen im Alltag reduziert.",
    },
    en: {
      title: "Appointment booking for medical practices | OnePractix",
      description:
        "Online appointment booking for medical practices that works reliably, structures patient access and reduces daily interruptions.",
    },
  },
  phone: {
    de: {
      title: "Telefonservice für Praxen | OnePractix",
      description:
        "Telefonservice für Praxen, der Anrufe zuverlässig annimmt, Termine strukturiert und den Empfang im Praxisalltag entlastet.",
    },
    en: {
      title: "Phone service for medical practices | OnePractix",
      description:
        "Phone service for medical practices that answers calls reliably, structures appointments and keeps patient communication calmer.",
    },
  },
  caseStudies: {
    de: {
      title: "Praxisbeispiele für Praxen | OnePractix",
      description:
        "Beispiele, wie Websites, Terminbuchung und Telefonservice Praxen im Alltag entlasten und Abläufe für Teams ruhiger machen.",
    },
    en: {
      title: "Practice examples for medical practices | OnePractix",
      description:
        "Examples of how websites, booking and phone handling reduce workload in medical practices and create calmer daily workflows.",
    },
  },
  about: {
    de: {
      title: "Über OnePractix – Dr. Moritz Buchgeher & Bob van den Boog",
      description:
        "OnePractix wurde von einem praktizierenden Arzt mitgegründet, um Praxen im Alltag mit klaren digitalen Abläufen verlässlich zu entlasten.",
    },
    en: {
      title: "About OnePractix – Dr. Moritz Buchgeher & Bob van den Boog",
      description:
        "OnePractix was co-founded by a practicing doctor to reduce administrative burden in daily practice through clear digital workflows.",
    },
  },
  contact: {
    de: {
      title: "Kontakt für Praxen | OnePractix",
      description:
        "Sprechen Sie mit uns darüber, wie Website, Termine, Anrufe und digitale Abläufe in Ihrer Praxis strukturiert betreut werden können.",
    },
    en: {
      title: "Contact for medical practices | OnePractix",
      description:
        "Talk to us about how your website, appointments, calls and digital workflows can be structured and looked after properly.",
    },
  },
  consultation: {
    de: {
      title: "Erstgespräch für Praxen | OnePractix",
      description:
        "Strukturiertes Erstgespräch mit OnePractix – 50 € pauschal, anrechenbar bei Zusammenarbeit. Per Zoom oder Telefon, in 30 Minuten.",
    },
    en: {
      title: "Consultation for medical practices | OnePractix",
      description:
        "Structured consultation with OnePractix, €50 flat fee, credited if we work together. Via Zoom or phone, in 30 minutes online.",
    },
  },
  whoItIsFor: {
    de: {
      title: "Für wen OnePractix geeignet ist | OnePractix",
      description:
        "OnePractix unterstützt Praxen, Kliniken und ausgewählte Gesundheitsanbieter bei digitaler Präsenz, Terminwegen, Erreichbarkeit und wiederkehrenden Anfragen.",
    },
    en: {
      title: "Who OnePractix is for | OnePractix",
      description:
        "OnePractix supports practices, clinics and selected healthcare providers with digital presence, appointment pathways, availability and recurring enquiries.",
    },
  },
};

/** Returns localized SEO title/description for a page key. */
export function getSEOContent(key: keyof typeof SEO_CONTENT, locale: Locale): PageSEO {
  return SEO_CONTENT[key][locale];
}

export function getHomepageJsonLd(locale: Locale = "de") {
  const isDE = locale === "de";
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ProfessionalService",
        "@id": ORG_ID,
        "name": "OnePractix",
        "url": BASE_URL,
        "logo": `${BASE_URL}/onepractix-logo-new.svg`,
        "description": isDE
          ? "OnePractix entlastet Praxen, indem Website, Terminorganisation und Erreichbarkeit strukturiert und verlässlich im Praxisalltag betreut werden."
          : "OnePractix relieves medical practices by structuring and reliably looking after website, appointment organisation and patient access in daily practice.",
        "slogan": isDE
          ? "Weniger Verwaltung. Mehr Zeit für Ihre Patienten."
          : "Less admin. More patient care.",
        "knowsAbout": isDE
          ? [
              "Praxisorganisation",
              "Terminorganisation",
              "Patientenkommunikation",
              "digitale Praxisabläufe",
            ]
          : [
              "Practice organisation",
              "Appointment organisation",
              "Patient communication",
              "Digital practice workflows",
            ],
        "founder": [
          { "@type": "Person", "@id": MORITZ_ID, "name": "Dr. Moritz Buchgeher" },
          { "@type": "Person", "@id": BOB_ID, "name": "Bob van den Boog" }
        ],
        "sameAs": [
          "https://linkedin.com/company/onepractix",
          "https://instagram.com/onepractix",
          "https://www.facebook.com/share/1Cvh8KrxyQ/?mibextid=wwXIfr"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "email": "info@onepractix.com",
          "contactType": "customer service",
          "availableLanguage": ["German", "English"]
        },
        "areaServed": [
          { "@type": "Country", "name": "Germany" },
          { "@type": "Country", "name": "Austria" },
          { "@type": "Country", "name": "Switzerland" }
        ]
      },
      {
        "@type": "WebSite",
        "@id": WEBSITE_ID,
        "name": "OnePractix",
        "url": BASE_URL
      },
      {
        "@type": "SiteNavigationElement",
        "name": isDE ? "Leistungen" : "Services",
        "url": urlFor("services", locale)
      },
      {
        "@type": "SiteNavigationElement",
        "name": isDE ? "Über uns" : "About",
        "url": urlFor("about", locale)
      },
      {
        "@type": "SiteNavigationElement",
        "name": isDE ? "Kontakt" : "Contact",
        "url": urlFor("contact", locale)
      },
      {
        "@type": "SiteNavigationElement",
        "name": isDE ? "Erstgespräch" : "Consultation",
        "url": urlFor("consultation", locale)
      },
      {
        "@type": "SiteNavigationElement",
        "name": isDE ? "Praxisbeispiele" : "Practice examples",
        "url": urlFor("caseStudies", locale)
      }
    ]
  };
}

export function getContactJsonLd(
  faqs?: { question: string; answer: string }[],
  locale: Locale = "de"
) {
  const url = urlFor("contact", locale);
  const graph: Record<string, unknown>[] = [
    {
      "@type": "ContactPage",
      "name": getSEOContent("contact", locale).title,
      "url": url,
      "inLanguage": locale,
      "publisher": { "@id": ORG_ID },
      "isPartOf": { "@id": WEBSITE_ID }
    }
  ];

  if (faqs && faqs.length > 0) {
    graph.push({
      "@type": "FAQPage",
      "url": url,
      "inLanguage": locale,
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    });
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph
  };
}

export function getWebPageJsonLd(
  name: string,
  description: string,
  url: string,
  locale: Locale = "de"
) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": name,
    "description": description,
    "url": url,
    "inLanguage": locale,
    "publisher": { "@id": ORG_ID },
    "isPartOf": { "@id": WEBSITE_ID }
  };
}

export function getAboutPageJsonLd(opts?: {
  moritzRole?: string;
  bobRole?: string;
  moritzImage?: string;
  bobImage?: string;
  locale?: Locale;
}) {
  const locale = opts?.locale ?? "de";
  const isDE = locale === "de";
  const moritzRole = opts?.moritzRole ?? "Co-Founder";
  const bobRole = opts?.bobRole ?? "Co-Founder";
  const moritzImage = opts?.moritzImage
    ? (opts.moritzImage.startsWith("http") ? opts.moritzImage : `${BASE_URL}${opts.moritzImage}`)
    : undefined;
  const bobImage = opts?.bobImage
    ? (opts.bobImage.startsWith("http") ? opts.bobImage : `${BASE_URL}${opts.bobImage}`)
    : undefined;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AboutPage",
        "name": isDE ? "Über OnePractix" : "About OnePractix",
        "url": urlFor("about", locale),
        "description": isDE
          ? "OnePractix wurde von einem Arzt mitgegründet und unterstützt Praxen dabei, ihre Abläufe rund um Website, Termine und Erreichbarkeit strukturiert zu betreuen."
          : "OnePractix was co-founded by a practicing doctor and supports practices in structuring their workflows around website, appointments and patient access.",
        "inLanguage": locale,
        "publisher": { "@id": ORG_ID },
        "isPartOf": { "@id": WEBSITE_ID },
        "about": [{ "@id": ORG_ID }]
      },
      {
        "@type": "ProfessionalService",
        "@id": ORG_ID,
        "name": "OnePractix",
        "url": BASE_URL,
        "founder": [{ "@id": MORITZ_ID }, { "@id": BOB_ID }]
      },
      {
        "@type": "Person",
        "@id": MORITZ_ID,
        "name": "Dr. Moritz Buchgeher",
        "jobTitle": moritzRole,
        ...(moritzImage ? { "image": moritzImage } : {}),
        "worksFor": { "@id": ORG_ID },
        "url": `${urlFor("about", locale)}#moritz-buchgeher`,
        "mainEntityOfPage": `${urlFor("about", locale)}#moritz-buchgeher`,
        "sameAs": [
          "https://www.linkedin.com/in/dr-moritz-buchgeher-703a32188/"
        ]
      },
      {
        "@type": "Person",
        "@id": BOB_ID,
        "name": "Bob van den Boog",
        "jobTitle": bobRole,
        ...(bobImage ? { "image": bobImage } : {}),
        "worksFor": { "@id": ORG_ID },
        "url": `${urlFor("about", locale)}#bob-van-den-boog`,
        "mainEntityOfPage": `${urlFor("about", locale)}#bob-van-den-boog`,
        "sameAs": [
          "https://www.linkedin.com/in/bob-van-den-boog-41a20ba5/"
        ]
      }
    ]
  };
}

export function getServicesJsonLd(locale: Locale = "de") {
  const isDE = locale === "de";
  const url = urlFor("services", locale);
  const provider = { "@id": ORG_ID };
  const areaServed = [
    { "@type": "Country", "name": "Germany" },
    { "@type": "Country", "name": "Austria" },
    { "@type": "Country", "name": "Switzerland" }
  ];
  const audience = {
    "@type": "Audience",
    "audienceType": isDE ? "Praxen und Kliniken" : "Medical practices and clinics"
  };

  const services = isDE
    ? [
        {
          name: "Praxis-Websites",
          description:
            "Praxis-Website wird strukturiert betreut, inhaltlich aktuell gehalten und im Praxisalltag mitgeführt – damit Patienten verlässlich informiert werden und der Erstkontakt ohne Aufwand für das Team gelingt.",
          serviceType: "Website-Erstellung und -Pflege für Arztpraxen",
        },
        {
          name: "Terminbuchung & Erreichbarkeit",
          description:
            "Terminorganisation und Erreichbarkeit werden laufend betreut: Online-Buchung, Erinnerungen, Chat und telefonische Terminannahme laufen verlässlich im Hintergrund mit und entlasten das Praxisteam spürbar.",
          serviceType: "Online-Terminbuchung und Patientenerreichbarkeit",
        },
        {
          name: "Sichtbarkeit & Online-Darstellung",
          description:
            "Die Online-Darstellung der Praxis wird kontinuierlich gepflegt und konsistent gehalten – dort, wo Patienten suchen, ohne dass sich das Team selbst um Profile, Einträge und Aktualisierungen kümmern muss.",
          serviceType: "Lokale Sichtbarkeit und Profilpflege für Praxen",
        },
      ]
    : [
        {
          name: "Practice websites",
          description:
            "The practice website is structured, kept up to date and looked after in daily practice – so patients are reliably informed and the first point of contact works without effort for the team.",
          serviceType: "Website creation and maintenance for medical practices",
        },
        {
          name: "Appointment booking & access",
          description:
            "Appointment organisation and access are continuously looked after: online booking, reminders, chat and phone appointment handling run reliably in the background and noticeably relieve the practice team.",
          serviceType: "Online appointment booking and patient access",
        },
        {
          name: "Visibility & online presence",
          description:
            "The online presence of the practice is continuously maintained and kept consistent – where patients search, without the team having to manage profiles, listings and updates themselves.",
          serviceType: "Local visibility and profile maintenance for practices",
        },
      ];

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "name": isDE ? "Leistungen" : "Services",
        "description": isDE
          ? "Website, Terminbuchung und Erreichbarkeit für Praxen – strukturiert und betreut."
          : "Websites, appointment booking and patient access for medical practices – structured and looked after by one accountable partner.",
        "url": url,
        "inLanguage": locale,
        "publisher": provider,
        "isPartOf": { "@id": WEBSITE_ID }
      },
      ...services.map((s) => ({
        "@type": "Service",
        "name": s.name,
        "serviceType": s.serviceType,
        "description": s.description,
        "provider": provider,
        "areaServed": areaServed,
        "audience": audience,
        "url": url
      }))
    ]
  };
}

export function getCaseStudiesJsonLd(locale: Locale = "de") {
  const isDE = locale === "de";
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "name": getSEOContent("caseStudies", locale).title,
        "url": urlFor("caseStudies", locale),
        "description": isDE
          ? "Beispiele aus dem Praxisalltag, wie OnePractix Abläufe rund um Website, Termine und Erreichbarkeit strukturiert betreut."
          : "Examples from daily practice showing how OnePractix structures and looks after workflows around website, appointments and patient access.",
        "inLanguage": locale,
        "publisher": { "@id": ORG_ID },
        "isPartOf": { "@id": WEBSITE_ID }
      }
    ]
  };
}

export function getFAQJsonLd(
  faqs: { question: string; answer: string }[],
  opts?: { pageUrl?: string; pageName?: string; locale?: Locale }
) {
  const locale = opts?.locale ?? "de";
  const pageUrl = opts?.pageUrl ?? urlFor("consultation", locale);
  const pageName =
    opts?.pageName ?? (locale === "de" ? "Erstgespräch – OnePractix" : "Consultation – OnePractix");

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "name": pageName,
        "url": pageUrl,
        "inLanguage": locale,
        "publisher": { "@id": ORG_ID },
        "isPartOf": { "@id": WEBSITE_ID }
      },
      {
        "@type": "FAQPage",
        "url": pageUrl,
        "inLanguage": locale,
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };
}

export function getBlogIndexJsonLd(
  posts: { slug: { de: string; en?: string }; title: string; excerpt: string; publishedAt: string; updatedAt?: string }[],
  locale: "de" | "en" = "de",
  page: number = 1,
) {
  const path = locale === "en" ? "/en/blog" : "/de/blog";
  const url = page > 1 ? `${BASE_URL}${path}?page=${page}` : `${BASE_URL}${path}`;
  const baseName = locale === "en"
    ? "Insights for medical practices | OnePractix"
    : "Einblicke für Praxen | OnePractix";
  const name = page > 1
    ? (locale === "en"
        ? `Insights for medical practices | Page ${page} | OnePractix`
        : `Einblicke für Praxen | Seite ${page} | OnePractix`)
    : baseName;
  const description = locale === "en"
    ? "Structured insights for patients and practices on digital practice organization, accessibility and appointment booking in daily care."
    : "Strukturierte Einblicke für Patienten und Praxen rund um digitale Praxisorganisation, Erreichbarkeit und Terminbuchung im Alltag.";
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "name": name,
        "url": url,
        "inLanguage": locale,
        "publisher": { "@id": ORG_ID },
        "isPartOf": { "@id": WEBSITE_ID },
        "description": description
      },
      ...posts.map((p) => ({
        "@type": "BlogPosting",
        "headline": p.title,
        "description": p.excerpt,
        "datePublished": p.publishedAt,
        "dateModified": p.updatedAt ?? p.publishedAt,
        "url": `${BASE_URL}${path}/${locale === "en" ? (p.slug.en ?? p.slug.de) : p.slug.de}`,
        "inLanguage": locale,
        "author": { "@id": ORG_ID },
        "publisher": { "@id": ORG_ID }
      }))
    ]
  };
}

export function getBlogPostJsonLd(post: {
  slug: string;
  title: string;
  excerpt: string;
  publishedAt: string;
  updatedAt?: string;
  metaDescription?: string;
  locale?: "de" | "en";
  hasTranslation?: boolean;
}) {
  const locale = post.locale ?? "de";
  const path = locale === "en" ? "/en/blog" : "/de/blog";
  const url = `${BASE_URL}${path}/${post.slug}`;
  const image =
    locale === "en"
      ? `${BASE_URL}/og-case-studies-en.jpg`
      : `${BASE_URL}/og-case-studies.jpg`;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.metaDescription ?? post.excerpt,
    "image": image,
    "datePublished": post.publishedAt,
    "dateModified": post.updatedAt ?? post.publishedAt,
    "url": url,
    "inLanguage": locale,
    "mainEntityOfPage": { "@type": "WebPage", "@id": url },
    "author": { "@id": ORG_ID },
    "publisher": { "@id": ORG_ID }
  };
}

/**
 * Localized labels used by BreadcrumbList JSON-LD. Kept in sync with the
 * visible page H1s / nav labels. Home is always first; Blog overview is used
 * as the parent for blog post breadcrumbs.
 */
const BREADCRUMB_LABELS: Record<RouteKey, { de: string; en: string }> = {
  home:         { de: "Startseite",        en: "Home" },
  services:     { de: "Leistungen",        en: "Services" },
  about:        { de: "Über uns",          en: "About" },
  caseStudies:  { de: "Praxisbeispiele",   en: "Practice examples" },
  contact:      { de: "Kontakt",           en: "Contact" },
  consultation: { de: "Erstgespräch",      en: "Consultation" },
  website:      { de: "Website für Praxen",        en: "Medical websites" },
  booking:      { de: "Terminbuchung",     en: "Appointment booking" },
  phone:        { de: "Telefonservice",    en: "Phone service" },
  privacy:      { de: "Datenschutz",       en: "Privacy" },
  imprint:      { de: "Impressum",         en: "Imprint" },
  terms:        { de: "AGB",               en: "Terms" },
  blog:         { de: "Einblicke",         en: "Insights" },
  whoItIsFor:   { de: "Für wen",           en: "Who it is for" },
};

function breadcrumbItem(position: number, name: string, item: string) {
  return {
    "@type": "ListItem",
    "position": position,
    "name": name,
    "item": item,
  };
}

/**
 * BreadcrumbList JSON-LD for a public, indexable, non-home page.
 * Always Home → currentPage. For blog posts, callers should use
 * `getBlogPostBreadcrumbJsonLd` instead.
 */
export function getBreadcrumbJsonLd(routeKey: RouteKey, locale: Locale = "de") {
  const homeLabel = BREADCRUMB_LABELS.home[locale];
  const currentLabel = BREADCRUMB_LABELS[routeKey][locale];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      breadcrumbItem(1, homeLabel, urlFor("home", locale)),
      breadcrumbItem(2, currentLabel, urlFor(routeKey, locale)),
    ],
  };
}

/**
 * BreadcrumbList JSON-LD for a single blog post: Home → Blog → article.
 */
export function getBlogPostBreadcrumbJsonLd(opts: {
  locale: Locale;
  postTitle: string;
  postUrl: string;
}) {
  const homeLabel = BREADCRUMB_LABELS.home[opts.locale];
  const blogLabel = BREADCRUMB_LABELS.blog[opts.locale];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      breadcrumbItem(1, homeLabel, urlFor("home", opts.locale)),
      breadcrumbItem(2, blogLabel, urlFor("blog", opts.locale)),
      breadcrumbItem(3, opts.postTitle, opts.postUrl),
    ],
  };
}
